import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaTagDescriptor } from "@components/MetaTags/MetaTags";
import { Route } from "@route-types/ZhHans/+types/ZhHansPage";
import { ZhHansPageContext } from "@routes/ZhHans/utils/types";
import { fetchZhHansPageData } from "@routes/ZhHans/ZhHansPage.server";
import ZhHansPageContent from "@routes/ZhHans/ZhHansPageContent";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { HttpStatusCode } from "axios";

/**
 * This is the main component for the zh-hans page.
 * @figma https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/WEB-BAU-2025?node-id=16171-54936&m=dev
 * */
const ZhHansPage = (): React.ReactElement => {
  const context = useLoaderData<ZhHansPageContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  return <ZhHansPageContent {...context.payload.data} />;
};

export default ZhHansPage;

export const loader = async () => await fetchZhHansPageData();

export const meta = ({ loaderData }: Route.MetaArgs): MetaTagDescriptor[] => {
  const context = loaderData as ZhHansPageContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return data.metaTags;
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: ZhHansPageContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
